import './App.css';
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Map, { MapRef, ViewStateChangeEvent, Source, Layer } from 'react-map-gl';
import mapboxgl from 'mapbox-gl';

import {
  Header,
  Loader,
  TrackerStatus
} from './components'

import AnimalPopup from './components/AnimalPopup';
import SpeciesButtonBar from './components/SpeciesButtonBar';

import { ANIMAL_GROUPS, ANIMAL_GROUP_COLOR_MAP, getDisplayAnimalGroupKey } from './utilities/animalGroups';
import { getDaysSince } from './utilities/time';
import {
  POPUP_OFFSET,
  PopupPlacement,
  getDesktopFocusOffset,
  getPopupPlacementForPoint,
  getViewportPointForOffset,
} from './utilities/popupPlacement';
import {
  hasIOSProjectionFallback,
  persistIOSProjectionFallback,
  shouldFallbackFromGlobe,
} from './utilities/projection';

// @ts-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax 
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

// Mapbox telemetry causes noisy blocked requests in local/dev environments and
// is not needed for app functionality.
if (typeof (mapboxgl as any).setTelemetryEnabled === 'function') {
  (mapboxgl as any).setTelemetryEnabled(false);
}

const tokenValue = process.env.REACT_APP_MAPBOX_TOKEN as string | undefined;

if (!tokenValue) {
  throw new Error('REACT_APP_MAPBOX_TOKEN is required');
}

const configValue: string = (process.env.REACT_APP_biotracker_service as string);
const mapStyleUrl = '/map-style.json';

type LockedPopupPlacement = PopupPlacement & { tagId: string };

const LazyPath = lazy(() => import('./components/Path'));
// Preload the Path chunk on module load to remove lazy-chunk fetch latency
// from the first path open.
void import('./components/Path');

function getSpeciesKey(value?: string) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

const HOME_POSITION = { lng: 145.974, lat: -16.791 };

function getAnimalSpeciesKey(animal: any) {
  return animal?.speciesKey || getSpeciesKey(animal?.species);
}

function getHistoryBounds(positions: Array<{ latitude: number; longitude: number }>) {
  const normalizedPoints = positions.map((position) => ({
    latitude: position.latitude,
    longitude: position.longitude >= 0 ? position.longitude : 360 + position.longitude,
  }));

  const latitudes = normalizedPoints.map((position) => position.latitude);
  const longitudes = normalizedPoints.map((position) => position.longitude);

  return [
    [Math.min(...longitudes), Math.min(...latitudes)],
    [Math.max(...longitudes), Math.max(...latitudes)],
  ] as [[number, number], [number, number]];
}

function normalizeMapLongitude(longitude: number) {
  const normalizedLongitude = longitude % 360;
  return normalizedLongitude >= 0 ? normalizedLongitude : normalizedLongitude + 360;
}

function isAtTargetHistoryView(map: mapboxgl.Map, targetCamera?: mapboxgl.CameraOptions | null) {
  if (!targetCamera?.center || typeof targetCamera.zoom !== 'number') {
    return false;
  }

  const currentCenter = map.getCenter();
  const targetCenter = mapboxgl.LngLat.convert(targetCamera.center);

  return Math.abs(currentCenter.lat - targetCenter.lat) <= 0.0005
    && Math.abs(normalizeMapLongitude(currentCenter.lng) - normalizeMapLongitude(targetCenter.lng)) <= 0.0005
    && Math.abs(map.getZoom() - targetCamera.zoom) <= 0.05;
}

function resolveSpeciesImageUrl(species: any, preferThumbnail = false) {
  const assetPath = preferThumbnail
    ? (species?.thumbnailUrl || species?.imageUrl || species?.thumbnail || species?.image || '')
    : (species?.imageUrl || species?.thumbnailUrl || species?.image || species?.thumbnail || '');
  if (!assetPath) {
    return '';
  }

  if (assetPath.startsWith('http://') || assetPath.startsWith('https://') || assetPath.startsWith('data:')) {
    return assetPath;
  }

  return `${configValue}${assetPath}`;
}


function App() {
  const ref = useRef<MapRef>(null);
  const MERCATOR_INTERACTION_ZOOM = 6;
  const isMobilePortrait = window.innerWidth <= 768 && window.innerHeight > window.innerWidth;
  const isMobile = isMobilePortrait;
  const isIOS = /iPad|iPhone|iPod/.test(window.navigator.userAgent)
    || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
  const [iosMercatorFallback, setIosMercatorFallback] = useState(() => isIOS && hasIOSProjectionFallback());

  const [loaded, setLoaded] = useState(false);
  const [mapSettled, setMapSettled] = useState(false);
  const [menuReady, setMenuReady] = useState(false);
  const [pointsReady, setPointsReady] = useState(false);
  const [hovered, setHovered] = useState<any | null>(null);
  const [opacity, setOpacity] = useState(1);
  const [viewZoom, setViewZoom] = useState(3);

  const [selected, setSelected] = useState<any | null>(null);
  const [position, setPosition] = useState(HOME_POSITION);

  const [path, setPath] = useState<any | null>(null);
  const [pathRenderKey, setPathRenderKey] = useState(0);
  const [hideOtherAnimals, setHideOtherAnimals] = useState(false);

  const [groups, setGroups] = useState<Array<any>>([]);
  const [species, setSpecies] = useState({})

  const mouseLeaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const speciesPrefetchRef = useRef<Set<string>>(new Set());
  const selectedRef = useRef<any>(null);
  const hoveredRef = useRef<any>(null);
  const animalsByTagIdRef = useRef<Record<string, any>>({});
  const moveRafRef = useRef<number | null>(null);
  const mouseMoveRafRef = useRef<number | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const touchMovedRef = useRef(false);
  const lastTouchSelectionAtRef = useRef(0);
  const pendingPathOpenTokenRef = useRef(0);
  const hasReplayedFirstPathMountRef = useRef(false);
  const [lockedPopupPlacement, setLockedPopupPlacement] = useState<LockedPopupPlacement | null>(null);

  const activePopup = selected ?? hovered;
  const activePopupTagId = activePopup?.tagId ?? null;
  // Try globe on iOS first, but fall back to mercator for the rest of the
  // session if Mapbox starts failing while globe is active.
  const projectionName = iosMercatorFallback || viewZoom >= MERCATOR_INTERACTION_ZOOM ? 'mercator' : 'globe';
  const popupCameraKey = `${position.lng}:${position.lat}:${viewZoom}`;

  const focusIndividual = useCallback((individual: any, duration = isMobile ? 1800 : 1000) => {
    const map = ref.current?.getMap();
    if (!map) return;

    const currentZoom = map.getZoom();
    const targetZoom = Math.max(currentZoom, 7);

    if (map.getProjection?.()?.name !== projectionName) {
      map.setProjection(projectionName);
    }

    const cameraOffset: [number, number] = isMobile ? [0, 140] : getDesktopFocusOffset(true);

    if (isMobile) {
      setLockedPopupPlacement(null);
    } else {
      setLockedPopupPlacement({
        tagId: String(individual.tagId),
        ...getPopupPlacementForPoint(getViewportPointForOffset(cameraOffset), true),
      });
    }

    const easeOptions: mapboxgl.FlyToOptions = {
      center: {
        lat: individual.position.latitude,
        lng: individual.position.longitude,
      },
      zoom: targetZoom,
      duration,
      offset: cameraOffset,
      essential: true
    };

    const runAnimation = () => {
      if (!isMobile) {
        map.once('moveend', () => {
          setLockedPopupPlacement((current) => current?.tagId === String(individual.tagId) ? null : current);
        });
      }

      if (isMobile) {
        map.flyTo(easeOptions);
      } else {
        map.easeTo(easeOptions);
      }
    };

    setTimeout(() => {
      if (map.isMoving() || map.isZooming() || map.isEasing()) {
        map.once('idle', runAnimation);
      } else {
        runAnimation();
      }
    }, 50);
  }, [isMobile, projectionName]);

  const popupPlacement = useMemo(() => {
    if (isMobile || !activePopup) {
      return { anchor: 'bottom' as const, offset: POPUP_OFFSET };
    }

    if (selected && lockedPopupPlacement?.tagId === String(activePopupTagId)) {
      return lockedPopupPlacement;
    }

    void popupCameraKey;

    const map = ref.current?.getMap();
    if (!map) {
      return { anchor: 'bottom' as const, offset: POPUP_OFFSET };
    }

    const point = map.project([Number(activePopup.position.longitude), Number(activePopup.position.latitude)]);
    return getPopupPlacementForPoint(point, selected !== null);
  }, [activePopup, activePopupTagId, isMobile, lockedPopupPlacement, popupCameraKey, selected]);

  const markMapReady = useCallback(() => {
    const map = ref.current?.getMap();
    if (!map) {
      return;
    }

    setViewZoom(map.getZoom());
    setLoaded(true);
  }, []);

  const selectIndividual = useCallback((individual: any) => {
    pendingPathOpenTokenRef.current += 1;
    selectedRef.current = individual;
    hoveredRef.current = null;
    setHovered(null);
    setSelected(individual);

    setPath(null);
  }, [])

  const showIndividualPath = useCallback((history: any) => {
    const bounds = getHistoryBounds(history.positions);
    const map = ref.current?.getMap();

    const isMobilePortrait = window.innerWidth <= 768 && window.innerHeight > window.innerWidth;
    const mobileVerticalPadding = isMobilePortrait
      ? {
          top: Math.min(220, Math.max(120, Math.round(window.innerHeight * 0.24))),
          bottom: Math.min(180, Math.max(96, Math.round(window.innerHeight * 0.18))),
        }
      : null;
    const padding = isMobilePortrait
      ? { top: mobileVerticalPadding!.top, right: 28, bottom: mobileVerticalPadding!.bottom, left: 28 }
      : { top: 80, right: 40, bottom: 100, left: 40 };

    if (!map) {
      setPath(history.positions);
      return;
    }

    const openToken = pendingPathOpenTokenRef.current + 1;
    pendingPathOpenTokenRef.current = openToken;
    let opened = false;

    const openPath = () => {
      if (opened || pendingPathOpenTokenRef.current !== openToken) {
        return;
      }

      opened = true;
      setPath(history.positions);

      // The first path open can hit a Mapbox source/layer race where the
      // initial GeoJSON update is swallowed. A remount gives Mapbox a clean
      // render cycle, matching the healing effect React StrictMode provides
      // in development. Only do this once per session.
      if (!hasReplayedFirstPathMountRef.current) {
        hasReplayedFirstPathMountRef.current = true;
        window.setTimeout(() => {
          if (pendingPathOpenTokenRef.current !== openToken) {
            return;
          }

          setPathRenderKey((current) => current + 1);
        }, 0);
      }
    };

    const targetCamera = map.cameraForBounds(bounds as mapboxgl.LngLatBoundsLike, { padding });

    if (!map.isMoving() && !map.isZooming() && !map.isEasing() && isAtTargetHistoryView(map, targetCamera)) {
      openPath();
      return;
    }

    map.fitBounds(bounds as mapboxgl.LngLatBoundsLike, { padding, duration: 1000 });

    if (isMobilePortrait) {
      openPath();
      return;
    }

    if (!map.isMoving() && !map.isZooming() && !map.isEasing()) {
      openPath();
      return;
    }

    map.once('moveend', openPath);
    window.setTimeout(openPath, 1150);
  }, [])

  async function initSpecies() {
    const resp = await fetch(`${configValue}/api/v1/species/catalog`);
    if (!resp.ok) {
      throw new Error(`Failed to load species (status ${resp.status})`);
    }
    const data = await resp.json();

    return data.reduce((previous: Record<string, any>, entry: any) => {
      previous[entry.speciesKey] = { ...entry, individuals: [] };
      return previous;
    }, {});
  }

  async function initGroups(speciesData) {
    const resp = await fetch(`${configValue}/api/v1/animals/map`);
    if (!resp.ok) {
      throw new Error(`Failed to load animals (status ${resp.status})`);
    }
    const animals = await resp.json();

    animals.forEach(animal => {
      const key = getAnimalSpeciesKey(animal);
      if (!(key in speciesData)) {
        speciesData[key] = {
          speciesKey: key,
          name: animal.species,
          scientificName: '',
          group: animal.group,
          description: '',
          protected: false,
          status: '',
          imageUrl: null,
          thumbnailUrl: null,
          individuals: [],
        };
      }

      speciesData[key].individuals.push(animal);
    });

    const nextGroups = ANIMAL_GROUPS.map((group) => ({
      ...group,
      entries: Object.values(speciesData).filter((entry: any) => getDisplayAnimalGroupKey(entry.group) === group.key)
    }));

    setSpecies(speciesData);
    setGroups(nextGroups);
  }

  useEffect(() => {
    (async () => {
      try {
        const speciesData = await initSpecies();
        await initGroups(speciesData);
      } catch (err) {
        console.error('Failed to initialise reef tracker data', err);
      }
    })();
  }, [])

  useEffect(() => {
    if (!loaded || !mapSettled) {
      return;
    }

    const activeSpecies = Object.values(species).filter((entry: any) => entry?.individuals?.length > 0);
    const urlsToPrefetch = activeSpecies
      .map((entry: any) => resolveSpeciesImageUrl(entry))
      .filter((url: string) => url.length > 0 && !speciesPrefetchRef.current.has(url));

    if (urlsToPrefetch.length === 0) {
      return;
    }

    const prefetchImage = (url: string, index: number) => {
      const delay = Math.min(index * 400, 4000);
      const timeoutId = window.setTimeout(() => {
        const image = new Image();
        image.decoding = 'async';
        try {
          (image as HTMLImageElement & { fetchPriority?: 'high' | 'low' | 'auto' }).fetchPriority = 'low';
        } catch (_) {}
        image.src = url;
        speciesPrefetchRef.current.add(url);
      }, delay);

      return timeoutId;
    };

    const schedulePrefetch = () => urlsToPrefetch.map(prefetchImage);

    const idleCallback = (window as any).requestIdleCallback as undefined | ((callback: () => void) => number);
    const cancelIdleCallback = (window as any).cancelIdleCallback as undefined | ((id: number) => void);
    let idleId: number | null = null;
    let timeoutIds: number[] = [];

    const kickoff = () => {
      if (idleCallback) {
        idleId = idleCallback(() => {
          timeoutIds = schedulePrefetch();
        });
        return;
      }

      timeoutIds = schedulePrefetch();
    };

    const startTimeoutId = window.setTimeout(kickoff, 2000);

    return () => {
      window.clearTimeout(startTimeoutId);
      if (cancelIdleCallback && idleId !== null) {
        cancelIdleCallback(idleId);
      }
      timeoutIds.forEach((timeoutId) => window.clearTimeout(timeoutId));
    };
  }, [loaded, mapSettled, species]);
  
  const animalsFlat = useMemo(
    () => {
      return groups.reduce((acc: Array<{ entry: any; group: any }>, group) => {
        return acc.concat(
          group.entries.reduce((acc2: Array<{ entry: any; group: any }>, species) => {
            if (species.individuals.length === 0) return acc2;
            return acc2.concat(species.individuals.map((entry: any) => ({ entry, group })));
          }, [])
        );
      }, []);
    },
    [groups]
  )

  const animalsGeoJSON = useMemo(() => ({
    type: 'FeatureCollection' as const,
    features: animalsFlat.map(({ entry, group }) => ({
      type: 'Feature' as const,
      id: entry.tagId,
      geometry: {
        type: 'Point' as const,
        coordinates: [entry.position.longitude, entry.position.latitude]
      },
      properties: {
        tagId: entry.tagId,
        active: getDaysSince(entry) <= 100,
        color: group.color,
      }
    }))
  }), [animalsFlat]);

  const hoverHitRadius = isMobile ? 12 : 10;
  const clickHitRadius = isMobile ? 12 : 12;
  const showInitialLoader = !loaded || !mapSettled || !menuReady || (animalsFlat.length > 0 && !pointsReady);

  const circleHoverHitPaint = useMemo(() => ({
    'circle-radius': hoverHitRadius,
    'circle-color': '#000000',
    'circle-opacity': 0.001,
    'circle-stroke-width': 0,
    'circle-pitch-scale': 'viewport' as const,
  }), [hoverHitRadius]);

  const circleClickHitPaint = useMemo(() => ({
    'circle-radius': clickHitRadius,
    'circle-color': '#000000',
    'circle-opacity': 0.001,
    'circle-stroke-width': 0,
    'circle-pitch-scale': 'viewport' as const,
  }), [clickHitRadius]);

  const circleBasePaint = useMemo(() => ({
    'circle-radius': 7,
    'circle-color': ['case',
      ['==', ['get', 'tagId'], activePopupTagId], '#ffffff',
      ['get', 'color']
    ] as any,
    'circle-opacity': selected !== null
      ? ['case', ['==', ['get', 'tagId'], selected.tagId], 1.0, 0.3] as any
      : 1.0,
    'circle-stroke-width': 1.5,
    'circle-stroke-color': 'rgba(0,0,0,0.2)',
    'circle-stroke-opacity': selected !== null
      ? ['case', ['==', ['get', 'tagId'], selected.tagId], 1.0, 0.3] as any
      : 1.0,
    'circle-pitch-scale': 'viewport' as const,
  }), [activePopupTagId, selected]);

  const getAnimalFromFeatures = useCallback((features: any[] | undefined, _point: { x: number; y: number }, layerIds: string[], _maxDistance?: number) => {
    if (!features?.length) return null;

    for (const feature of features) {
      if (!layerIds.includes(feature.layer?.id)) continue;
      const animal = animalsByTagIdRef.current[String(feature.properties?.tagId)];
      if (animal) {
        return animal;
      }
    }

    return null;
  }, []);

  const queryAnimalAtPoint = useCallback((point: { x: number; y: number }, layers: string[], radii: number[]) => {
    const map = ref.current?.getMap();
    if (!map) return null;
    if (!layers.every((layerId) => map.getLayer(layerId))) return null;

    for (const radius of radii) {
      const bbox: [[number, number], [number, number]] = [
        [point.x - radius, point.y - radius],
        [point.x + radius, point.y + radius],
      ];
      // Note: Use immediate query, but filter carefully by the point's pixels
      const features = map.queryRenderedFeatures(bbox, { layers });
      const animal = getAnimalFromFeatures(features as any[] | undefined, point, layers, radius);
      if (animal) {
        return animal;
      }
    }

    return null;
  }, [getAnimalFromFeatures]);

  const closeActivePanels = useCallback(() => {
    if (mouseLeaveTimerRef.current) {
      clearTimeout(mouseLeaveTimerRef.current);
      mouseLeaveTimerRef.current = null;
    }
    pendingPathOpenTokenRef.current += 1;
    selectedRef.current = null;
    hoveredRef.current = null;
    setHovered(null);
    setSelected(null);
    setPath(null);
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resetToHomeView = useCallback(() => {
    closeActivePanels();
    setHideOtherAnimals(false);
    
    const map = ref.current?.getMap();
    if (map) {
      map.flyTo({
        center: {
          lat: HOME_POSITION.lat,
          lng: HOME_POSITION.lng,
        },
        zoom: 3,
        duration: 1000,
        essential: true,
      });
    }
  }, [closeActivePanels]);

  const handleSelection = useCallback((hitAnimal: any | null) => {
    if (path) {
      return;
    }

    if (hitAnimal) {
      if (selectedRef.current?.tagId === hitAnimal.tagId) {
        closeActivePanels();
        return;
      }
      selectIndividual(hitAnimal);
      
      // Defers the camera movement to the next tick to prevent the 
      // current touch/click event from immediately cancelling the animation.
      requestAnimationFrame(() => {
        focusIndividual(hitAnimal);
      });
      return;
    }

    closeActivePanels();
  }, [closeActivePanels, focusIndividual, path, selectIndividual]);

  useEffect(() => {
    if (selected !== null) return;
    setLockedPopupPlacement(null);
  }, [selected]);

  // Keep the tag lookup in sync for click and hover hit-testing.
  useEffect(() => {
    animalsByTagIdRef.current = Object.fromEntries(
      animalsFlat.map(({ entry }) => [String(entry.tagId), entry])
    );
  }, [animalsFlat]);

  // Animate the pulse ring for active markers
  useEffect(() => {
    if (!loaded) return;
    let animFrame: number;
    const startTime = performance.now();
    const DURATION = 5000; // total cycle length; only the dead pause changed, burst speed below is unchanged
    const ACTIVE_DURATION_MS = 750; // how long the ping burst itself takes, unchanged
    const ACTIVE = ACTIVE_DURATION_MS / DURATION;
    function animate(time: number) {
      const t_phase = ((time - startTime) % DURATION) / DURATION;
      const map = ref.current?.getMap();
      if (map?.isStyleLoaded() && map.getLayer('animals-pulse')) {
        try {
          if (t_phase < ACTIVE) {
            const t = t_phase / ACTIVE;
            map.setPaintProperty('animals-pulse', 'circle-radius', 7 + t * 15);
            map.setPaintProperty('animals-pulse', 'circle-stroke-opacity', 0.8 * (1 - t));
          } else {
            // Dead time — ring invisible, reset radius ready for next cycle
            map.setPaintProperty('animals-pulse', 'circle-radius', 7);
            map.setPaintProperty('animals-pulse', 'circle-stroke-opacity', 0);
          }
        } catch (_) {}
      }
      animFrame = requestAnimationFrame(animate);
    }
    animFrame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animFrame);
  }, [loaded]);

  useEffect(() => {
    const map = ref.current?.getMap();
    if (!loaded || !map || !isIOS || iosMercatorFallback) return;

    const fallbackToMercator = (reason: string, error?: Error) => {
      if (map.getProjection?.()?.name !== 'globe') return;
      if (error && !shouldFallbackFromGlobe(error)) return;

      persistIOSProjectionFallback(true);
      setIosMercatorFallback(true);

      try {
        map.setProjection('mercator');
      } catch (_) {}

      if (error) {
        console.warn(`Falling back to mercator on iOS after ${reason}.`, error);
      } else {
        console.warn(`Falling back to mercator on iOS after ${reason}.`);
      }
    };

    const handleMapError = (event: mapboxgl.ErrorEvent) => {
      fallbackToMercator('a Mapbox runtime error', event.error);
    };

    const handleContextLoss = () => {
      fallbackToMercator('a WebGL context loss');
    };

    map.on('error', handleMapError);
    map.on('webglcontextlost', handleContextLoss);

    return () => {
      map.off('error', handleMapError);
      map.off('webglcontextlost', handleContextLoss);
    };
  }, [iosMercatorFallback, isIOS, loaded]);

  useEffect(() => {
    const map = ref.current?.getMap();
    if (!loaded || !map) return;
    if (map.getProjection?.()?.name === projectionName) return;
    map.setProjection(projectionName);
  }, [loaded, projectionName]);

  // Forward wheel events that land on the popup back to the map canvas
  // so scroll-to-zoom still works when the cursor is over an animal card.
  useEffect(() => {
    function forwardWheelToMap(e: WheelEvent) {
      const target = e.target as Element | null;
      if (!target?.closest?.('.animal-popup')) return;
      const canvas = ref.current?.getMap().getCanvas();
      if (!canvas) return;
      canvas.dispatchEvent(new WheelEvent('wheel', {
        bubbles: true,
        cancelable: true,
        deltaMode: e.deltaMode,
        deltaX: e.deltaX,
        deltaY: e.deltaY,
        deltaZ: e.deltaZ,
        ctrlKey: e.ctrlKey,   // needed for trackpad pinch-zoom
        altKey: e.altKey,
        shiftKey: e.shiftKey,
        metaKey: e.metaKey,
        clientX: e.clientX,
        clientY: e.clientY,
      }));
    }
    document.addEventListener('wheel', forwardWheelToMap);
    return () => document.removeEventListener('wheel', forwardWheelToMap);
  }, []);

  const clearHovered = useCallback(() => {
    hoveredRef.current = null;
    setHovered(null);
  }, []);

  const scheduleHoverClear = useCallback(() => {
    if (selectedRef.current !== null) return;
    if (mouseLeaveTimerRef.current) return;
    const snapshot = hoveredRef.current;
    mouseLeaveTimerRef.current = setTimeout(() => {
      if (hoveredRef.current === snapshot) {
        clearHovered();
      }
      mouseLeaveTimerRef.current = null;
    }, 300);
  }, [clearHovered]);

  useEffect(() => {
    if (isMobile) return;

    function handleDocumentMouseMove(event: MouseEvent) {
      if (mouseMoveRafRef.current !== null) return;
      mouseMoveRafRef.current = requestAnimationFrame(() => {
        mouseMoveRafRef.current = null;

        if (selectedRef.current !== null) return;

        const map = ref.current?.getMap();
        if (!map) return;
        const canvas = map.getCanvas();
        const rect = canvas.getBoundingClientRect();
        const point = {
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        };

        const insideCanvas = point.x >= 0 && point.y >= 0 && point.x <= rect.width && point.y <= rect.height;
        if (!insideCanvas) {
          canvas.style.cursor = '';
          scheduleHoverClear();
          return;
        }

        const overUiOverlay = !!(event.target as Element | null)?.closest?.('.header-shell, .button-container, .popup-container');
        if (overUiOverlay) {
          canvas.style.cursor = '';
          if (mouseLeaveTimerRef.current) {
            clearTimeout(mouseLeaveTimerRef.current);
            mouseLeaveTimerRef.current = null;
          }
          clearHovered();
          return;
        }

        const nearest = queryAnimalAtPoint(point, ['animals-hover-hit'], [0, 4, 8, hoverHitRadius]);
        if (nearest) {
          canvas.style.cursor = 'pointer';
          if (mouseLeaveTimerRef.current) {
            clearTimeout(mouseLeaveTimerRef.current);
            mouseLeaveTimerRef.current = null;
          }
          if (hoveredRef.current !== nearest) {
            hoveredRef.current = nearest;
            setHovered(nearest);
          }
          return;
        }

        canvas.style.cursor = '';
        const overPopup = !!(event.target as Element | null)?.closest?.('.mapboxgl-popup');
        if (!overPopup) {
          scheduleHoverClear();
        }
      });
    }

    document.addEventListener('mousemove', handleDocumentMouseMove);
    return () => document.removeEventListener('mousemove', handleDocumentMouseMove);
  }, [clearHovered, hoverHitRadius, isMobile, queryAnimalAtPoint, scheduleHoverClear]);

  function onMove(event: ViewStateChangeEvent) {
    if (moveRafRef.current !== null) return;
    moveRafRef.current = requestAnimationFrame(() => {
      moveRafRef.current = null;
      setPosition({
        lat: ref.current!.getCenter().lat,
        lng: ref.current!.getCenter().lng,
      });
    });
  }

  function onZoom(event: ViewStateChangeEvent) {
    setViewZoom(event.viewState.zoom);
    if (event.viewState.zoom < 3) return setOpacity(1);
    setOpacity(Math.max(0, 1 - (event.viewState.zoom - 3)))
  }

  function onPathExit() {
    pendingPathOpenTokenRef.current += 1;
    setHideOtherAnimals(false);
    setPath(null);
  }

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return;
      if (path) {
        setHideOtherAnimals(false);
        setPath(null);
      } else {
        closeActivePanels();
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [path, closeActivePanels]);

  if (groups.length === 0) return (<Loader fullScreen={true} />)

  return (
    <div className="App">
      <Header 
        position={position} 
        opacity={opacity} 
        showTitle={selected === null && path === null} 
        hideOnMobile={selected !== null || path !== null}
        onLogoClick={resetToHomeView}
      />
      <TrackerStatus tracking={groups.reduce((p, c) => p + c.entries.length, 0)} style={{ opacity: selected === null && path === null ? opacity : 0 }} />
      <Map
        ref={ref}
        initialViewState={{
          latitude: position.lat,
          longitude: position.lng,
          zoom: 3,
          bearing: 0,
          pitch: 0,
        }}
        projection={projectionName as 'globe' | 'mercator'}
        mapLib={mapboxgl}
        mapStyle={mapStyleUrl}
        maxZoom={12}
        mapboxAccessToken={tokenValue}
        interactiveLayerIds={loaded ? ['animals-hover-hit', 'animals-click-hit'] : undefined}
        fog={{
          "range": [0.8, 8],
          "color": "#111111",
          "horizon-blend": 0.005,
          "space-color": "#000000",
          "star-intensity": 0.1
        }}
        style={{ position: "absolute" }}
        onMouseLeave={() => {
          if (isMobile) return;
          const canvas = ref.current?.getMap().getCanvas();
          if (canvas) canvas.style.cursor = '';
          scheduleHoverClear();
        }}
        onTouchStart={(event) => {
          if (event.originalEvent.touches.length !== 1) {
            touchStartRef.current = null;
            touchMovedRef.current = true;
            return;
          }
          touchStartRef.current = { x: event.point.x, y: event.point.y };
          touchMovedRef.current = false;
        }}
        onTouchMove={(event) => {
          if (!touchStartRef.current) return;
          if (event.originalEvent.touches.length !== 1) {
            touchMovedRef.current = true;
            return;
          }
          const moved = Math.hypot(
            event.point.x - touchStartRef.current.x,
            event.point.y - touchStartRef.current.y,
          ) > 10;
          if (moved) {
            touchMovedRef.current = true;
          }
        }}
        onTouchEnd={(event) => {
          const touchStart = touchStartRef.current;
          touchStartRef.current = null;
          if (!touchStart || touchMovedRef.current) {
            touchMovedRef.current = false;
            return;
          }
          touchMovedRef.current = false;
          lastTouchSelectionAtRef.current = Date.now();
          
          // Only query features that are actually under the touch point.
          // queryAnimalAtPoint already does a small bbox search.
          const hitAnimal = queryAnimalAtPoint(event.point, ['animals-click-hit'], [0, 4, 8]);
          
          handleSelection(hitAnimal);
        }}
        onClick={(event) => {
          if (Date.now() - lastTouchSelectionAtRef.current < 500) {
            return;
          }

          // Only query features that are actually under the click point.
          const hitAnimal = queryAnimalAtPoint(event.point, ['animals-click-hit'], [0, 4, 8]);

          handleSelection(hitAnimal);
        }}
        onMove={onMove}
        onZoom={onZoom}
        onIdle={() => {
          setMapSettled(true);

          const map = ref.current?.getMap();
          if (!map || !map.isStyleLoaded() || !map.getLayer('animals-base')) {
            return;
          }

          const renderedPoints = animalsFlat.length === 0
            ? 0
            : map.queryRenderedFeatures(undefined, { layers: ['animals-base'] }).length;

          if (animalsFlat.length === 0 || renderedPoints > 0) {
            setPointsReady(true);
          }
        }}
        onStyleData={markMapReady}
        onLoad={markMapReady}>
        {groups.length > 0 && (
          <Source id="animals" type="geojson" data={animalsGeoJSON}>
            <Layer
              id="animals-hover-hit"
              type="circle"
              paint={circleHoverHitPaint}
            />
            <Layer
              id="animals-click-hit"
              type="circle"
              paint={circleClickHitPaint}
            />
            {/* Pulse ring — animates via setPaintProperty in the RAF effect */}
            <Layer
              id="animals-pulse"
              type="circle"
              filter={['==', ['get', 'active'], true]}
              layout={{ visibility: (path && hideOtherAnimals) ? 'none' : 'visible' }}
              paint={{
                'circle-radius': 7,
                'circle-color': 'rgba(0,0,0,0)' as any,
                'circle-opacity': 0,
                'circle-stroke-width': 3,
                'circle-stroke-color': ['get', 'color'] as any,
                'circle-stroke-opacity': 0.8,
                'circle-pitch-scale': 'viewport',
                'circle-radius-transition': { duration: 0, delay: 0 } as any,
                'circle-stroke-opacity-transition': { duration: 0, delay: 0 } as any,
              }}
            />
            {/* Main dot */}
            <Layer
              id="animals-base"
              type="circle"
              layout={{ visibility: (path && hideOtherAnimals) ? 'none' : 'visible' }}
              paint={circleBasePaint}
            />
          </Source>
        )}

        {activePopup && !path && (
          <AnimalPopup
            key={`${activePopup.tagId}:${selected !== null ? 'selected' : 'hover'}`}
            selected={activePopup}
            species={species[getAnimalSpeciesKey(activePopup)]}
            expanded={selected !== null}
            anchor={popupPlacement.anchor}
            offset={popupPlacement.offset}
            onClose={() => {
              closeActivePanels();
            }}
            onDrawClick={showIndividualPath}
          />
        )}
        {path && selected && (
          <Suspense fallback={null}>
            <LazyPath
              key={pathRenderKey}
              selected={selected}
              species={species[getAnimalSpeciesKey(selected)]}
              path={path}
              color={ANIMAL_GROUP_COLOR_MAP[selected.group]}
              onExitPath={onPathExit}
              onHideOthers={setHideOtherAnimals} />
          </Suspense>
        )}
      </Map>
      {
        !path && (
          <SpeciesButtonBar
            groups={groups}
            isMobile={isMobile}
            onReady={() => {
              setMenuReady(true);
            }}
            onOpenMenu={closeActivePanels}
            onSelected={(individual) => {
              selectIndividual(individual);
              focusIndividual(individual, isMobile ? 1200 : 3000);
            }}
          />
        )
      }
      {showInitialLoader && (
        <div className="app-loader-overlay">
          <Loader fullScreen={true} />
        </div>
      )}
    </div >
  );
}

export default App;
